import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { brand } from "../../context";
import { useApoAuth } from "../../hooks";
import { getChatRoomByRoomCode } from "../MyProfile/Quotations/api";
import { TripSummaryCard } from "../QuotationRequestChat/components/FinalizeQuotationRequestModal/TripSummaryCard";
import {
  getChatRoomMessages,
  getQuotationChatWebSocketUrl,
  getQuotationRequestByReference,
} from "./api";
import { Chat } from "./components/Chat";
import { ConnectionStatusIndicator } from "./components/ConnectionStatusIndicator";
import type { WebSocketMessage } from "./types";

// Mock trip summary for layout testing
const mockTripSummary = `# Trip Summary

## Destination
Greece - Santorini & Mykonos

## Travel Dates
Late June / Early July 2024 (10 days)

## Travelers
2 adults

## Itinerary Overview

### Santorini (3-4 nights)
- **Accommodation**: Luxury hotel with caldera views
- **Activities**: 
  - Sunset viewing in Oia
  - Wine tasting tours
  - Beach visits
  - Volcanic island tours

### Mykonos (2-3 nights)
- **Accommodation**: Beachfront resort
- **Activities**:
  - Beach hopping
  - Nightlife experiences
  - Windmill tours
  - Delos island excursion

## Services Included
- Round-trip flights
- Hotel accommodations
- Airport transfers
- Island ferry transfers
- Selected activities and tours

## Budget Range
Mid to Luxury accommodations`;

export function QuotationChat() {
  const history = useHistory();
  const location = useLocation();
  const { user, source_entity, getIdTokenClaims, isAuthenticated } = useApoAuth();

  // Get room_code from URL params
  const params = new URLSearchParams(location.search);
  const roomCode = params.get("room_code");
  const reference = params.get("reference");

  // Fetch quotation request by reference
  const { data: quotationRequestData } = useQuery({
    queryKey: ["quotationRequest", reference],
    queryFn: async () => {
      if (!reference || !isAuthenticated) {
        throw new Error("Reference and authentication are required");
      }
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Unable to get authentication token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;
      return getQuotationRequestByReference({ reference, token });
    },
    enabled: !!reference && isAuthenticated,
    refetchOnWindowFocus: false,
  });

  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const chatPanelRef = useRef<HTMLDivElement>(null);

  const username = user?.name || user?.email || "";
  const userEmail = user?.email || "";

  // Fetch room information from fastchat using room_code
  const { data: roomData, isLoading: isLoadingRoom } = useQuery({
    queryKey: ["chatRoom", roomCode],
    queryFn: () => {
      if (!roomCode) {
        throw new Error("Room code is required");
      }
      return getChatRoomByRoomCode(roomCode);
    },
    enabled: !!roomCode,
    refetchOnWindowFocus: false,
  });

  // Extract chat_session_uid from room data
  // The chat_session_uid is the room UID
  const chatSessionUid = roomData?.data?.uid;

  // Find the member_session_uid that matches the current user's email
  // Format: member_session_{uuid}___{email}
  const memberSessionUid = useMemo(() => {
    if (!roomData?.data?.member_session_uids || !userEmail) {
      return null;
    }
    return (
      roomData.data.member_session_uids.find((uid: string) => uid.includes(`___${userEmail}`)) ||
      null
    );
  }, [roomData?.data?.member_session_uids, userEmail]);

  // Get websocket URL - only connect if we have both room UID and member session UID
  const getSocketUrl = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (!chatSessionUid || !memberSessionUid) {
          throw new Error("Chat session UID and member session UID are required");
        }
        const url = getQuotationChatWebSocketUrl(chatSessionUid, memberSessionUid);
        resolve(url);
      }, 100);
    });
  }, [chatSessionUid, memberSessionUid]);

  // Fetch chat room messages
  const { data: chatRoomMessagesRaw } = useQuery({
    queryKey: ["chat-room-messages", chatSessionUid],
    queryFn: () => getChatRoomMessages(chatSessionUid as string),
    enabled: !!chatSessionUid,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  const chatRoomMessages = chatRoomMessagesRaw?.data ?? [];

  // WebSocket connection - only connect if we have both room UID and member session UID
  const { readyState, lastJsonMessage, sendJsonMessage } = useWebSocket(
    chatSessionUid && memberSessionUid ? getSocketUrl : null,
    {
      shouldReconnect: () => !!chatSessionUid && !!memberSessionUid,
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    },
  );

  // Load chat room messages when they're fetched
  useEffect(() => {
    if (!chatRoomMessages.length) return;
    setMessages(chatRoomMessages);
  }, [chatRoomMessages]);

  // Handle incoming websocket messages
  useEffect(() => {
    if (!lastJsonMessage) return;

    const wsMessage = lastJsonMessage as WebSocketMessage;

    // Check if message already exists to avoid duplicates
    if (messages.some((m) => m.uid === wsMessage.uid)) return;

    setMessages((prev) => [...prev, wsMessage]);
  }, [lastJsonMessage, messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!messages.length) return;
    if (!chatPanelRef.current) return;
    chatPanelRef.current.scrollTo({
      top: chatPanelRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const handleSendMessage = useCallback(
    (message: string) => {
      if (!chatSessionUid || !username || !userEmail || !source_entity || !roomData?.data) {
        console.error("Missing required data for sending message");
        return;
      }

      // Create message payload matching fastchat MessageDef pattern
      const messagePayload: WebSocketMessage = {
        uid: crypto.randomUUID(),
        content: message,
        room_uid: roomData.data.uid,
        timestamp: Math.floor(Date.now() / 1000), // Unix timestamp
        member: {
          username,
          email: userEmail,
          role: "client", // User sending message is always client
          source_entity: source_entity,
        },
      };

      sendJsonMessage(messagePayload);

      // Optimistically add user message to UI
      setMessages((prev) => [...prev, messagePayload]);
    },
    [chatSessionUid, username, userEmail, source_entity, roomData, sendJsonMessage],
  );

  const connectionStatus = useMemo(() => {
    return {
      [ReadyState.CONNECTING]: "connecting",
      [ReadyState.OPEN]: "open",
      [ReadyState.CLOSING]: "closing",
      [ReadyState.CLOSED]: "closed",
      [ReadyState.UNINSTANTIATED]: "uninstantiated",
    }[readyState];
  }, [readyState]);

  const isDarkMode = false; // Can be made dynamic later
  const tripSummary = quotationRequestData?.data?.client_message || mockTripSummary;

  return (
    <div className="QuotationChat mt-[var(--navbar-height)] h-[calc(100vh-var(--navbar-height))] grid grid-cols-1 md:grid-cols-2!">
      {/* Left Column - Chat */}
      <div className="bg-white grid grid-rows-[max-content_1fr] h-full overflow-hidden">
        <div className="p-3 border-b border-gray-200 grid grid-cols-[1fr_max-content]">
          <div className="grid gap-2 grid-cols-[max-content_max-content] items-center">
            <h4 className="font-bold">Chat</h4>
            {chatSessionUid && <ConnectionStatusIndicator connectionStatus={connectionStatus} />}
          </div>
          <button
            type="button"
            onClick={() => history.goBack()}
            className="Button"
            data-brand={brand}
          >
            Back
          </button>
        </div>
        <div className="min-h-0" ref={chatPanelRef}>
          {isLoadingRoom ? (
            <div className="grid place-items-center h-full text-gray-500">Loading...</div>
          ) : chatSessionUid && memberSessionUid ? (
            <Chat
              messages={messages}
              onSendMessage={handleSendMessage}
              disabled={connectionStatus !== "open"}
              memberEmail={userEmail}
              sourceEntity={source_entity || ""}
            />
          ) : (
            <div className="grid place-items-center h-full text-gray-500">
              {!chatSessionUid
                ? "Chat session not found for this quotation"
                : !memberSessionUid
                  ? "Member session not found for your account"
                  : "Unable to connect to chat"}
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Trip Summary */}
      <div className="hidden md:block! bg-gray-50 border-r border-gray-200 overflow-y-auto">
        <div className="p-6">
          <TripSummaryCard
            tripSummary={tripSummary}
            isDarkMode={isDarkMode}
            showDownloadButton={false}
            sessionId={null}
          />
        </div>
      </div>
    </div>
  );
}
